import { ChevronRightIcon } from "@heroicons/react/20/solid";
import type { ReactNode } from "react";
import { useId, useState } from "react";

interface CollapsibleSectionProps {
  title: string;
  /** Short status shown next to the title while collapsed and expanded, e.g. counts. */
  summary?: ReactNode;
  defaultOpen?: boolean;
  /** Controlled mode: when given, the parent owns the open state. */
  open?: boolean;
  onToggle?: (open: boolean) => void;
  children: ReactNode;
  className?: string;
}

/**
 * A titled panel whose body can be folded away. The whole header is the toggle,
 * so the summary text stays scannable when several sections are stacked and
 * the reader only opens the one they need.
 */
export function CollapsibleSection({
  title,
  summary,
  defaultOpen = false,
  open: controlledOpen,
  onToggle,
  children,
  className = "",
}: CollapsibleSectionProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = controlledOpen ?? uncontrolledOpen;
  const contentId = useId();

  const toggle = () => {
    const next = !open;
    if (controlledOpen === undefined) setUncontrolledOpen(next);
    onToggle?.(next);
  };

  return (
    <section
      className={`bg-gray-800/75 border border-gray-700/50 rounded-lg ${className}`}
      data-testid={`collapsible-${slug(title)}`}
    >
      <h2 className="m-0">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-controls={contentId}
          className="w-full flex items-center gap-3 px-5 py-4 text-left rounded-lg hover:bg-gray-700/40 transition-colors"
        >
          <ChevronRightIcon
            aria-hidden="true"
            className={`h-5 w-5 flex-shrink-0 text-gray-400 transition-transform ${
              open ? "rotate-90" : ""
            }`}
          />
          <span className="text-base font-medium text-white">{title}</span>
          {summary && (
            <span className="ml-auto text-sm text-gray-400 text-right truncate">
              {summary}
            </span>
          )}
        </button>
      </h2>
      {open && (
        <div id={contentId} className="px-5 pb-5">
          {children}
        </div>
      )}
    </section>
  );
}

function slug(title: string) {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}
